/**
 * PlanSelect — compact resale-plan drop-down for the T1 Plan / T2 Plan columns.
 *
 * The open menu shows each option's full label (e.g. "DARLINGS TRUCK CERTIFIED")
 * but the collapsed trigger shows only the bare code (e.g. "TCD") to save grid
 * width — behavior a native <select> can't provide (its closed display always
 * equals the selected option's text), hence this small custom control.
 *
 * Options come from the location's USED Types list (`useNutSheetTypes().usedTypes`)
 * because a trade-in becomes used inventory; the stored value is the type code.
 * The menu is positioned with `position: fixed` so it isn't clipped by the
 * table's horizontal-scroll (`overflow-x-auto`) container.
 */

import React from 'react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useNutSheetTypes } from '../../hooks/useNutSheetTypes';
import { useAnchoredMenu } from '../../hooks/useAnchoredMenu';

interface PlanSelectProps {
  locationId: number;
  value: string | null;
  onChange: (code: string) => void;
  disabled?: boolean;
}

export const PlanSelect: React.FC<PlanSelectProps> = ({
  locationId,
  value,
  onChange,
  disabled,
}) => {
  const { usedTypes, loading } = useNutSheetTypes(locationId);
  const { open, setOpen, pos, btnRef, menuRef } = useAnchoredMenu();

  const select = (code: string) => {
    onChange(code);
    setOpen(false);
    // Return focus to the trigger (native-<select> behavior). The click focused
    // the menu's option button, and unmounting a focused node fires no blur —
    // without this, the row's blur-commit never runs and the selection is
    // silently never saved.
    btnRef.current?.focus();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((o) => !o)}
        title={value || 'Select resale plan'}
        className="flex items-center justify-between w-16 rounded border border-gray-500 bg-white px-1 py-0.5 text-xs disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600"
      >
        <span className={value ? 'font-medium' : 'text-gray-400'}>{value || '—'}</span>
        <ChevronUpDownIcon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.width, maxHeight: pos.maxHeight }}
          className="z-50 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-xs shadow-lg"
        >
          <button
            type="button"
            onClick={() => select('')}
            className="block w-full px-3 py-1 text-left text-gray-400 hover:bg-gray-100"
          >
            — (clear)
          </button>
          {usedTypes.length === 0 && (
            <div className="px-3 py-1 italic text-gray-400">No USED types configured</div>
          )}
          {usedTypes.map((t) => (
            <button
              key={t.code}
              type="button"
              onClick={() => select(t.code)}
              className={`block w-full px-3 py-1 text-left hover:bg-gray-100 ${
                value === t.code ? 'bg-blue-50 font-medium' : ''
              }`}
            >
              {t.label} <span className="text-gray-400">({t.code})</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
};
